import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { Plus, Wrench, X } from "lucide-react";

const STATUS_COLORS = {
  Open: "bg-[#E0E7FF] text-[#1E3A8A]",
  "Under classification": "bg-[#FFF3CD] text-[#856404]",
  Classified: "bg-[#D1ECF1] text-[#0C5460]",
  "Returned to advisor": "bg-[#D4EDDA] text-[#155724]",
  Closed: "bg-[#E2E8F0] text-[#495057]",
  Cancelled: "bg-[#F8D7DA] text-[#721C24]",
};

export default function ClassificationRequestsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [v2enums, setV2enums] = useState(null);
  const [enums, setEnums] = useState(null);
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
    const { data } = await api.get(`/classification-requests${q}`);
    setItems(data);
    return data;
  };

  useEffect(() => {
    api.get("/v2/enums").then((r) => setV2enums(r.data));
    api.get("/enums").then((r) => setEnums(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/service-cases?open_only=true").then((r) => setCases(r.data));
  }, []);
  useEffect(() => { load(); }, [statusFilter]);

  // ?focus=<id> now redirects to the full-page detail. Iter 48: the drawer
  // is removed from the list page; only the SOM→PCP creation panel keeps
  // the slide-in behaviour (that panel lives in ServiceCaseDetailPage).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    const from = params.get("from");
    if (!focusId) return;
    const suffix = from ? `?from=${from}` : "";
    navigate(`/classification-requests/${focusId}${suffix}`, { replace: true });
  }, [navigate]);

  const classifiers = useMemo(() => users.filter((u) => ["classifier", "workshop_supervisor", "workshop_coordinator"].includes(u.role)), [users]);

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="classification-page">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#1F3864]" />
            Classificação de Peças
          </h1>
          <p className="text-xs text-[#6C757D]">Revisões técnicas pedidas pelos advisors e encaminhadas ao classificador da oficina.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1"
          data-testid="new-classification-btn"
        >
          <Plus className="w-3 h-3" /> Novo pedido
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-[#6C757D] uppercase tracking-widest">Estado</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded-sm text-sm"
          data-testid="cr-status-filter"
        >
          <option value="">Todos</option>
          {v2enums?.classification_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto text-xs text-[#6C757D]">{items.length} pedido{items.length === 1 ? "" : "s"}</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Nº</th>
              <th className="px-3 py-2 text-left">Obra</th>
              <th className="px-3 py-2 text-left">Cliente / Veículo</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Aberta</th>
              <th className="px-3 py-2 text-right">Abrir</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-3 py-4 text-center text-xs text-[#6C757D]">Sem pedidos de classificação.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-gray-100 hover:bg-[#F8FAFC] cursor-pointer" onClick={() => navigate(`/classification-requests/${it.id}`)} data-testid={`cr-row-${it.id}`}>
                <td className="px-3 py-2 font-mono text-[#1F3864]">{it.number}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  {it.service_case_id ? (
                    <Link to={`/service-cases/${it.service_case_id}`} onClick={(e) => e.stopPropagation()} className="text-[#1F3864] hover:underline" data-testid={`cr-som-link-${it.id}`}>
                      {it.service_case_number}
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-3 py-2">{it.customer_name} <span className="text-[#6C757D] font-mono text-xs">· {it.vehicle_registration}</span></td>
                <td className="px-3 py-2 text-xs">{it.classification_type}</td>
                <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                <td className="px-3 py-2 text-xs text-[#6C757D]">{fmtDateTime(it.opened_at)}</td>
                <td className="px-3 py-2 text-right text-xs"><button className="text-[#1F3864] hover:underline" data-testid={`cr-open-${it.id}`}>Abrir</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && v2enums && enums && (
        <CreateDialog
          v2enums={v2enums}
          classifiers={classifiers}
          cases={cases}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[status] || "bg-[#E2E8F0] text-[#495057]"}`}>{status}</span>;
}

function CreateDialog({ v2enums, classifiers, cases, onClose, onSaved }) {
  const [form, setForm] = useState({
    service_case_id: cases[0]?.id || "",
    classification_type: v2enums.classification_types[0],
    description: "",
    classifier_id: classifiers[0]?.id || "",
    priority: "Normal",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/classification-requests", form);
      onSaved();
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="cr-create-dialog">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-base font-bold">Novo pedido de classificação</h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Obra (SOM) *">
            <select value={form.service_case_id} onChange={(e) => setForm({ ...form, service_case_id: e.target.value })} className={inputCls} data-testid="cr-case-select">
              <option value="">Seleccione…</option>
              {cases.map((c) => <option key={c.id} value={c.id}>{c.number} · {c.customer_name} · {c.vehicle_registration}</option>)}
            </select>
          </Field>
          <Field label="Tipo de classificação *">
            <select value={form.classification_type} onChange={(e) => setForm({ ...form, classification_type: e.target.value })} className={inputCls}>
              {v2enums.classification_types.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Atribuir a classificador">
            <select value={form.classifier_id} onChange={(e) => setForm({ ...form, classifier_id: e.target.value })} className={inputCls} data-testid="cr-classifier-select">
              <option value="">(sem atribuição)</option>
              {classifiers.map((u) => <option key={u.id} value={u.id}>{`${u.name} · ${u.role.replaceAll("_", " ")}`}</option>)}
            </select>
          </Field>
          <Field label="Descrição *">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={inputCls} data-testid="cr-description" />
          </Field>
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="cr-error">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancelar</button>
          <button onClick={submit} disabled={busy || !form.service_case_id || !form.description.trim()} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="cr-submit">
            {busy ? "A gravar…" : "Criar pedido"}
          </button>
        </div>
      </div>
    </div>
  );
}


const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="text-sm text-[#1A1A2E]">{value || "—"}</div>
    </div>
  );
}
